/**
 * The route sheet's field set (Art. 2, Order FOM/1230/2013), as the spine of the
 * passenger screens — the passenger twin of `deca/fields.ts`.
 *
 * `column` stays `snake_case`: one name links the import template, the API, the
 * PDF and a support conversation.
 *
 * 🚨 `onPdf` is **true** for `conductor_nombre` and `conductor_nif`, which is the
 * exact inverse of the goods rule where `nif_conductor` is captured and never
 * printed. Art. 2(e) requires it; the client instructed it in writing after our
 * contrary recommendation and its reason were put to them, and it is recorded as
 * a Controller instruction in Addendum No. 2 §4.2. Do not "fix" this to match
 * goods, and do not relax the goods rule to match this. See the two entries
 * themselves, below, for the same warning at the data.
 */

import type { components } from '@deca/api'

/**
 * The route sheet's four numbered sections.
 *
 * A parallel type to the goods `FieldSection`, not a shared one — not because
 * the type system would otherwise stop a goods section from being used in a
 * passenger slot (it would not: the two unions already share the literal
 * members `'transportista'` and `'transporte'`), but because Orden
 * FOM/1230/2013 (passenger) and Orden FOM/2861/2012 (goods) are different legal
 * instruments that get amended independently. Two small dictionaries mean a
 * change to one Order's sections never touches the other's.
 */
export type PassengerSection = 'transportista' | 'contratante' | 'servicios' | 'transporte'

export const PASSENGER_SECTIONS: readonly PassengerSection[] = [
  'transportista',
  'contratante',
  'servicios',
  'transporte',
] as const

export const PASSENGER_SECTION_NUMBER: Record<PassengerSection, number> = {
  transportista: 1,
  contratante: 2,
  servicios: 3,
  transporte: 4,
}

/** Where a value lives: on the sheet, or per service (BOE Sixth). */
export type PassengerScope = 'document' | 'service'

export interface PassengerFieldDef {
  column: string
  /** The Order's own Spanish wording. A legal fact, so it lives here and never in the catalogues. */
  labelEs: string
  section: PassengerSection
  scope: PassengerScope
  mandatory: boolean
  /** Whether the value is printed on the public PDF. See the 🚨 above. */
  onPdf: boolean
  /** Mandatory only for these service classes; absent means unconditional. */
  mandatoryForClasses?: readonly ServiceClassValue[]
  multiline?: boolean
}

/**
 * Aliased from the frozen contract rather than hand-copied, so a class the
 * contract adds shows up here — and in any exhaustive `switch` over it — without
 * anyone having to remember to update this file.
 */
export type ServiceClassValue = components['schemas']['ServiceClass']

export const SERVICE_CLASSES = [
  'DISCRECIONAL',
  'REFUERZO',
  'COLABORACION',
  'TURISTICO',
] as const satisfies readonly ServiceClassValue[]

export const PASSENGER_FIELDS: readonly PassengerFieldDef[] = [
  { column: 'transportista_nombre', labelEs: 'Empresa transportista', section: 'transportista', scope: 'document', mandatory: true, onPdf: true },
  // The catalogue label for `transportista_nif` is the goods dictionary's own
  // entry for that column name (no passenger duplicate was added — see
  // es.json/en.json), so `contratante_nif` just below has to be spelled to match
  // it: both are "NIF" in Spanish and both read "Tax ID (NIF)" in English, which
  // is the house spelling `cargador_nif` and `nif_conductor` already use. They
  // sit in adjacent cards on the create form and the detail screen, so two
  // spellings of one concept were visible side by side until 2026-09-11.
  { column: 'transportista_nif', labelEs: 'NIF', section: 'transportista', scope: 'document', mandatory: true, onPdf: true },
  { column: 'contratante_nombre', labelEs: 'Contratante del servicio', section: 'contratante', scope: 'document', mandatory: true, onPdf: true },
  { column: 'contratante_nif', labelEs: 'NIF', section: 'contratante', scope: 'document', mandatory: true, onPdf: true },

  { column: 'servicio_origen', labelEs: 'Origen', section: 'servicios', scope: 'service', mandatory: true, onPdf: true },
  { column: 'servicio_destino', labelEs: 'Destino', section: 'servicios', scope: 'service', mandatory: true, onPdf: true },
  { column: 'servicio_fecha', labelEs: 'Fecha del servicio', section: 'servicios', scope: 'service', mandatory: true, onPdf: true },
  { column: 'servicio_clase', labelEs: 'Naturaleza del servicio', section: 'servicios', scope: 'service', mandatory: true, onPdf: true },
  // Art. 2(c) — intermediate stops, for the two classes that carry them.
  { column: 'servicio_paradas', labelEs: 'Paradas intermedias', section: 'servicios', scope: 'service', mandatory: false, onPdf: true, mandatoryForClasses: ['DISCRECIONAL', 'TURISTICO'], multiline: true },
  // Art. 2(f) — identify the reinforced or collaborated regular service.
  { column: 'servicio_clase_ref', labelEs: 'Servicio regular reforzado o colaborado', section: 'servicios', scope: 'service', mandatory: false, onPdf: true, mandatoryForClasses: ['REFUERZO', 'COLABORACION'] },

  { column: 'matricula_autobus', labelEs: 'Matrícula del autobús', section: 'transporte', scope: 'document', mandatory: true, onPdf: true },
  // Art. 2(e) — printed on the PDF: the exact inverse of the goods
  // `nif_conductor` rule (onPdf: false). Deliberate and client-instructed — see
  // the docblock above. Do not "fix" this to match goods.
  { column: 'conductor_nombre', labelEs: 'Nombre del conductor', section: 'transporte', scope: 'document', mandatory: true, onPdf: true },
  // Art. 2(e) — printed on the PDF, same inversion as `conductor_nombre` above.
  { column: 'conductor_nif', labelEs: 'NIF del conductor', section: 'transporte', scope: 'document', mandatory: true, onPdf: true },
] as const

const BY_COLUMN = new Map(PASSENGER_FIELDS.map((field) => [field.column, field]))

export function passengerFieldDef(column: string): PassengerFieldDef | undefined {
  return BY_COLUMN.get(column)
}

export function passengerFieldsForSection(section: PassengerSection): PassengerFieldDef[] {
  return PASSENGER_FIELDS.filter((field) => field.section === section)
}

/** Mandatory now, given the class chosen on this service row. */
export function isMandatoryForClass(
  def: PassengerFieldDef,
  clase: ServiceClassValue | '',
): boolean {
  if (def.mandatory) return true
  if (!def.mandatoryForClasses || !clase) return false
  return def.mandatoryForClasses.includes(clase)
}

/**
 * What an in-place amendment may touch — `PassengerDecaModifiableFields` in the
 * frozen contract, and nothing beyond it.
 *
 * No party is in this set. Changing `transportista_*` or `contratante_*` forces
 * a supersede under BOE Fifth.2, exactly as changing a goods party does.
 *
 * `'services'` names the whole per-service array, not a single field — it has
 * no matching `column` in `PASSENGER_FIELDS`. So
 * `PASSENGER_FIELDS.filter(f => PASSENGER_MODIFIABLE_COLUMNS.has(f.column))`
 * returns three entries, not four.
 */
export const PASSENGER_MODIFIABLE_COLUMNS: ReadonlySet<string> = new Set([
  'matricula_autobus',
  'conductor_nombre',
  'conductor_nif',
  'services',
])
